import * as React from 'react';
import { connect } from 'react-redux';
import { Container, Form, FormGroup, Input, Modal, ModalBody } from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';

import * as AuthAction from '../../actions/Auth';

import * as AuthApi from '../../api/auth';

import Loader from '../../components/Loader';
import Localization from '../../config/localization';

import Notifications from '../../lib/notifications';
import { GetMe, ToString } from '../../lib/utils';

import { PASSWORD_MIN_LENGTH } from '../../constants/constants';

import './Login.scss';

type IProps = Login.IProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

class Login extends React.Component<IProps, Login.IState> {
  state = {
    password: '',
    usernameOrEmail: '',

    forgotPasswordModal: false,

    loading: false,
    loadingMessage: ''
  };

  renderForgotPasswordModal = () => {
    return (
      <Modal centered isOpen={this.state.forgotPasswordModal} toggle={this._closeForgotPassword}>
        <ModalBody className="px-4 pt-4">
          <h4 className="eon-title eon-darkgrey">{Localization.loginForgotPasswordTitle}</h4>
          <h6 className="eon-title eon-middle-grey  mt-2">{Localization.loginForgotPasswordTips}</h6>

          <Form onSubmit={this._doForgotPassword} className="my-4">
            <FormGroup className="pt-2">
              <Input
                type="email"
                name="usernameOrEmail"
                id="usernameOrEmail"
                className="w-80 eon-input-text-no-label"
                onChange={this._setValue}
                placeholder={Localization.loginForgotPasswordAccountPlaceholder}
                required
              />
            </FormGroup>
            <div className="d-flex mt-4 pt-2">
              <button className="eon-button bg-eon-red eon-white mr-5">
                <span className="px-3">{Localization.loginForgotPasswordChangeButton}</span>
              </button>
              <button type="button" className="eon-button bg-eon-lightgrey eon-middle-grey mx-4" onClick={this._closeForgotPassword}>
                <span className="px-3">{Localization.loginForgotPasswordCloseButton}</span>
              </button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    );
  };

  render() {
    return (
      <Container fluid className="p-0 d-flex login-container">
        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        {this.renderForgotPasswordModal()}

        <div className="login-image w-75 h-80">
          <h1 className="eon-title third-field eon-white wrap-pre-line text-right mr-5 mt-5 pt-5">{Localization.loginTitle}</h1>
        </div>
        <div className="bg-eon-red text-left w-25 login-form h-80 d-flex">
          <div className="p-5 mt-auto mb-5 w-100 h-100 form-container">
            <h4 className="eon-title eon-white">{Localization.loginFormLabel}</h4>
            <Form onSubmit={this._doSubmit} className="mt-4">
              <FormGroup>
                <Input
                  type="text"
                  name="usernameOrEmail"
                  id="usernameOrEmail"
                  className="w-80 eon-input-text-no-label eon-red"
                  onChange={this._setValue}
                  placeholder={Localization.loginAccountPlaceholder}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  type="password"
                  name="password"
                  id="password"
                  className="w-80 eon-input-text-no-label eon-red"
                  onChange={this._setValue}
                  placeholder={Localization.loginPasswordPlaceholder}
                  minLength={PASSWORD_MIN_LENGTH}
                />
              </FormGroup>
              <div className="d-flex mt-3">
                <button className="login-button eon-button bg-eon-white eon-red">
                  <span className="login-button-span">{Localization.loginSubmitButton}</span>
                </button>
                <a className="p-0 h-100 ml-4 my-auto c-pointer eon-white" onClick={this._setForgotPassword}>
                  {Localization.loginForgotPassword}
                </a>
              </div>
            </Form>
          </div>
        </div>
      </Container>
    );
  }

  _doForgotPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();

      if (!this.state.usernameOrEmail) {
        return undefined;
      }

      await this.setState({ loading: true });

      const response = await AuthApi.ForgotPassword({ email: this.state.usernameOrEmail });

      if (response.data && response.data.error) {
        throw response.data.error;
      } else if (response.data && response.data.status !== 'sent') {
        throw new Error(Localization.Errors.default);
      }

      return this.setState({ loading: false, forgotPasswordModal: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _doSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();

      if (this.state.loading) {
        return undefined;
      }

      await this.setState({ loading: true });

      const data = { password: ToString(this.state.password), usernameOrEmail: ToString(this.state.usernameOrEmail) } as Login.ILoginParams;

      const response = await AuthApi.Login(data);

      if (!response.data || !response.data.accessToken) {
        throw new Error();
      }

      await this.setState({ loading: false });

      this.props.AuthUser(response.data.accessToken);

      return GetMe();
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _closeForgotPassword = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event && event.preventDefault) event.preventDefault();

    return this.setState({ forgotPasswordModal: false });
  };

  _setForgotPassword = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    return this.setState({ forgotPasswordModal: true });
  };

  _setValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const element = event.currentTarget || event.target || {};

    return this.setState(prevState => ({ ...prevState, [element.id]: element.value }));
  };
}

const mapStateToProps = (state: General.Store) => ({ Auth: state.Auth });

const mapDispatchToProps = (dispatch: Dispatch) => ({ ...bindActionCreators({ ...AuthAction }, dispatch) });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Login);
